import { useDispatch, useSelector } from "react-redux";

import { useFetchAdminAreaHierarchy } from "@/api/adminArea/userAdminAreaAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import {
  useMutationDatasetMetadata,
  usePoiInputRowsStatsTargets
} from "@/api/dataset/useDatasetAPI";
import { useMutateGetWidgetDefaultConfig } from "@/api/widget/useWidgetAPI";
import { MAX_POI } from "@/constants/dataset";
import { useGlobalAdminAreaSync } from "@/hooks/globalAdminArea/useGlobalFilterAdminArea";
import { useDataset } from "@/hooks/map/useDataset";
import { useSpatialInputRowsLimit } from "@/hooks/spatialAnalysis/useSpatialInputRowsLimit";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import { toggleUseGlobalFilter } from "@/store/slice/datasetSlice";
import type { DatasetPoiParameter } from "@/types/poiParameter";
import type { AdminAreaSettings } from "@/types/spatial";
import { getAvailableDatasets } from "@/utils/dataset";

import { buildPoiStreamFilters, isAdminAreaSameAsGlobal } from "./utils";

/**
 * Hook that encapsulates the confirm flow for the POI dataset confirmation modal.
 *
 * When the user confirms, it:
 * 1. Fetches dataset metadata for each dataset (sequentially)
 * 2. Streams the dataset data with the POI-specific filters
 * 3. Adds the dataset to the map via `handleAddDataset`
 * 4. Optionally syncs the selected admin area to the global filter if `shouldApplyGlobal` is true
 * 5. Determines whether the new datasets should be linked to the global filter based on the selected admin area and `shouldApplyGlobal` flag
 * 6. When linking to the global filter, it dispatches `toggleUseGlobalFilter` for each new dataset to link it to the global filter
 */

interface HandleConfirmDatasetsParams {
  poiDatasets: DatasetPoiParameter[];
  selectedAdminArea: AdminAreaSettings | null;
  selectedAdminAreaIds?: string[];
  shouldApplyGlobal?: boolean;
  isOverrideGlobalFilter?: boolean;
}

/**
 * Reactive inputs for the input-rows limit check. All optional so the
 * imperative confirm flow (and existing tests) can still call
 * `usePoiConfirmation()` with no args — the limit check stays disabled when no
 * datasets are supplied.
 */
interface UsePoiConfirmationProps {
  /** The POI datasets that will be added (selection picks + already-added). */
  poiDatasets?: DatasetPoiParameter[];
  selectedAdminArea?: AdminAreaSettings | null;
  selectedAdminAreaIds?: string[];
  /** Run the limit check only while the confirmation modal is open. */
  enabled?: boolean;
}

export const usePoiConfirmation = ({
  poiDatasets = [],
  selectedAdminArea = null,
  selectedAdminAreaIds = [],
  enabled = false
}: UsePoiConfirmationProps = {}) => {
  const dispatch = useDispatch();
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();
  const {
    handleAddDataset,
    handleAddDatasetShowCatchment,
    generateDatasetData
  } = useDataset();
  const { syncAdminAreaToGlobalFilter } = useGlobalAdminAreaSync();
  const fetchHierarchy = useFetchAdminAreaHierarchy();
  const {
    isAdminTreeEnabled,
    isParameterPOIForCatchmentEnabled,
    isDatasetWidgetEnabled
  } = useAppFeatures();

  const { datasets } = useSelector((state: RootState) => state.dataset);
  const { showCatchmentPopup } = useSelector((state: RootState) => state.ui);
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const {
    adminAreaSettings: globalAdminAreaSettings,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);

  const { mutateAsync: fetchWidgetDefaultConfig } =
    useMutateGetWidgetDefaultConfig();

  const existingCount = Object.values(getAvailableDatasets(datasets)).length;

  /**
   * Processes a list of POI datasets sequentially:
   * fetches their data via the stream API and adds them to the map.
   *
   * @param poiDatasets - Flat list of `DatasetPoiParameter` to add
   * @param selectedAdminArea - The currently selected admin area (used when the admin tree is disabled)
   * @param selectedAdminAreaIds - The list of selected admin area IDs (used when the admin tree is enabled)
   * @param shouldApplyGlobal - Whether to sync the selected admin area to the global filter after adding datasets
   * @param isOverrideGlobalFilter - Whether to prevent linking the new datasets to the global filter, even if the selected admin area matches the global filter
   */
  const handleConfirmDatasets = async ({
    poiDatasets,
    selectedAdminArea,
    selectedAdminAreaIds = [],
    shouldApplyGlobal = false,
    isOverrideGlobalFilter = false
  }: HandleConfirmDatasetsParams): Promise<void> => {
    if (!poiDatasets.length) return;
    const isAdminAreaMatchingGlobal = isAdminAreaSameAsGlobal({
      isAdminTreeEnabled,
      selectedAdminArea,
      selectedAdminAreaIds,
      globalAdminAreaSettings,
      globalSelectedAreaIds
    });

    // Stable per-confirmation: compute once outside the loop
    const shouldLinkToGlobalFilter =
      (isAdminAreaMatchingGlobal || shouldApplyGlobal) &&
      !isOverrideGlobalFilter;

    /**
     * Processes a list of POI datasets sequentially **in reverse order** so that
     * each `handleAddDataset` call (which prepends to `visualizationOrder`) results
     * in the original input order being preserved in the final visualization list.
     */
    for (const [reversedIndex, dataset] of [...poiDatasets]
      .reverse()
      .entries()) {
      const index = poiDatasets.length - 1 - reversedIndex;
      const metadata = await getDatasetMetadata({ id: dataset.dataset_id });
      const countryId = metadata?.country_id ?? "";
      const hierarchy = countryId ? await fetchHierarchy(countryId) : [];

      const filters = buildPoiStreamFilters({
        dataset,
        metadata,
        hierarchy,
        isAdminTreeEnabled,
        selectedAdminArea
      });

      const res = await fetchDatasetStream({
        datasetId: dataset.dataset_id,
        filters,
        generateDatasetData
      });

      let widgetDefaultConfig;
      // Skip the widget config fetch entirely when the Dataset Widget feature
      // is disabled so we don't hit the endpoint for an unused feature.
      if (isDatasetWidgetEnabled) {
        try {
          widgetDefaultConfig = await fetchWidgetDefaultConfig({
            id: dataset.dataset_id
          });
        } catch (error) {
          console.warn("Failed to fetch widget default config", error);
        }
      }

      if (!res?.rawDataset || !res?.datasetMetadata) continue;

      // Catchment popup flow: stage the POI dataset as a catchment input
      // (treated as POI) instead of adding it to the analysis canvas. Mirrors
      // the Data Explore catchment path in `DatasetRightAttrFilter`. In
      // catchment mode we never fall through to the canvas add — datasets over
      // the legacy POI limit (when the POI-for-catchment flag is off) are
      // simply not staged; the row-count limit check already blocks confirming
      // an over-limit set.
      if (
        showCatchmentPopup &&
        catchmentSettingsTemp?.datasetConfig &&
        (isParameterPOIForCatchmentEnabled ||
          res.rawDataset.rows.length <= MAX_POI)
      ) {
        /** When the selection lines up with the global admin area (or the
         * user opted to apply it), mark the catchment dataset as global so
         * the save flow can echo is_global_admin_area=true and the BE-side
         * global filter applies — same as the canvas path's
         * `toggleUseGlobalFilter`. */
        handleAddDatasetShowCatchment({
          dataset: res.datasetMetadata,
          rawDataset: res.rawDataset,
          attributesSchema: metadata?.schema ?? [],
          filters,
          batchIndex: index,
          is_global_admin_area: shouldLinkToGlobalFilter
        });
      } else {
        const newDataset = await handleAddDataset({
          // DatasetMetadata is structurally compatible with DatasetDetailResponse
          datasetMetadata: {
            ...res.datasetMetadata
          },
          datasetData: res.rawDataset,
          savedFilters: filters,
          attributesSchema: metadata?.schema ?? [],
          index: existingCount + index,
          // New POI datasets default to the "Simple" Color Scale per PRD.
          styleConfig:
            res.datasetMetadata.spatial_aggregation_type === "poi"
              ? { colorScaleMethod: "simple" }
              : undefined,
          widget: widgetDefaultConfig?.data.length
            ? widgetDefaultConfig.data.map((widget) => ({
                ...widget,
                is_default: true,
                label: ""
              }))
            : undefined
        });

        if (newDataset?.data?.id) {
          dispatch(
            toggleUseGlobalFilter({
              datasetId: newDataset.data.id,
              useGlobalFilter: shouldLinkToGlobalFilter
            })
          );
        }
      }
    }

    // `shouldApplyGlobal` is only true after the user explicitly confirmed the
    // "change global filter" dialog, so syncing the selected admin area to the
    // global filter is intentional for both the canvas and catchment paths.
    if (shouldApplyGlobal) {
      await syncAdminAreaToGlobalFilter({
        adminArea: selectedAdminArea,
        adminAreaIds: selectedAdminAreaIds,
        isAdminTreeEnabled
      });
    }
  };

  // --- Input-rows limit check -------------------------------------------------
  // Total = existing canvas datasets (summed internally by
  // `useSpatialInputRowsLimit` from `inputRowCounts`) + the POI datasets about
  // to be added. `usePoiInputRowsStatsTargets` builds each target's `/stats`
  // filter with the SAME `buildPoiStreamFilters` the stream uses, so the
  // payloads can't drift.
  const isLimitActive = enabled && poiDatasets.length > 0;

  const { data: statsTargets = [], isFetching: isPreparingTargets } =
    usePoiInputRowsStatsTargets({
      poiDatasets,
      selectedAdminArea,
      selectedAdminAreaIds,
      isAdminTreeEnabled,
      enabled: isLimitActive
    });

  const isEnabledQueriesStatusCount =
    isLimitActive && statsTargets.length > 0 && isAdminTreeEnabled;

  const { isOverLimit, limit, isFetchingStatusDataset } =
    useSpatialInputRowsLimit({
      statsTargets,
      enabled: isEnabledQueriesStatusCount
    });

  return {
    handleConfirmDatasets,
    isOverLimit,
    limit,
    isFetchingStatusDataset,
    isPreparingTargets: isLimitActive && isPreparingTargets
  };
};
